import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Icon,
  Theme,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import {
  ActivityOverview_LeaderFragment,
  DegreeSubject,
  DegreeType,
  TeacherCertificateDiscipline,
} from "@outschool/gql-frontend-generated";
import { faChevronDown, faInfoCircle, faMedal } from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import {
  TEACHER_CERTIFICATE_STATE_MAP,
  useCredentialString,
} from "@outschool/ui-components-website";
import _ from "lodash";
import React from "react";

import { YellowBlobIcon } from "./IconWithBackground";

type Credential = ActivityOverview_LeaderFragment["credentials"][0];
type CertificateStateKeys = keyof typeof TEACHER_CERTIFICATE_STATE_MAP;

export default function Credentials({
  credentials,
  isOrg,
}: {
  credentials: Credential[];
  isOrg: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\Credentials",
  );

  const {
    translateDegreeType,
    translateDegreeSubject,
    translateCredentialDiscipline,
    translateCertificateState,
  } = useCredentialString();

  if (!credentials?.length) {
    return null;
  }

  const degreeOrder = {
    [DegreeType.DoctorOfMedicine]: 1,
    [DegreeType.JurisDoctor]: 2,
    [DegreeType.DoctoralDegree]: 3,
    [DegreeType.MastersDegree]: 4,
    [DegreeType.BachelorsDegree]: 5,
    [DegreeType.AssociatesDegree]: 6,
  };

  const deduplicateCredentials = (credentials: Credential[]): Credential[] => {
    const degrees = credentials.filter(
      c => !!c.degreeType && !c.certificateDiscipline,
    );
    const certificates = credentials.filter(c => !!c.certificateDiscipline);

    // Group credentials by degree type and certificate discipline
    const credentialsByDegreeType = _.groupBy(degrees, "degreeType");
    const certificatesByDiscipline = _.groupBy(
      certificates.filter(c => !!c.certificateDiscipline),
      "certificateDiscipline",
    );

    const isUsefulSubject = (cred: Credential): boolean =>
      !!cred.degreeSubject && cred.degreeSubject !== DegreeSubject.Other;

    // Deduplicate degrees
    const deduplicatedDegrees = Object.values(credentialsByDegreeType)
      .map((group: Credential[]) => {
        const hasUsefulSubjectInCredentials = group.some(isUsefulSubject);

        if (hasUsefulSubjectInCredentials) {
          group = group.filter(isUsefulSubject);
        } else {
          // Case where one has a null degree subject and one has an "Other" subject
          const hasNullSubject = group.some(
            cred => cred.degreeSubject === null,
          );
          const hasOtherSubject = group.some(
            cred => cred.degreeSubject === DegreeSubject.Other,
          );

          if (hasNullSubject && hasOtherSubject) {
            group = group.filter(cred => cred.degreeSubject !== null);
          }
        }

        return _.uniqBy(group, "degreeSubject");
      })
      .flat();

    // Deduplicate certificates
    const deduplicatedCertificates = Object.values(certificatesByDiscipline)
      .map(group => {
        return _.uniqBy(group, "certificateState");
      })
      .flat();

    return [...deduplicatedDegrees, ...deduplicatedCertificates];
  };

  const sortedCredentials = isOrg
    ? [...credentials].sort((a, b) => {
        if (a.degreeType && b.degreeType) {
          return degreeOrder[a.degreeType] - degreeOrder[b.degreeType];
        }
        return 0;
      })
    : deduplicateCredentials([...credentials]).sort((a, b) => {
        if (a.degreeType && b.degreeType) {
          return degreeOrder[a.degreeType] - degreeOrder[b.degreeType];
        }
        return 0;
      });

  const associates = sortedCredentials.filter(
    c => c.degreeType === DegreeType.AssociatesDegree,
  );

  const bachelors = sortedCredentials.filter(
    c => c.degreeType === DegreeType.BachelorsDegree,
  );

  const grad = sortedCredentials.filter(
    c =>
      c.degreeType === DegreeType.MastersDegree ||
      c.degreeType === DegreeType.DoctoralDegree ||
      c.degreeType === DegreeType.JurisDoctor ||
      c.degreeType === DegreeType.DoctorOfMedicine,
  );
  const certs = sortedCredentials.filter(c => !!c.certificateDiscipline);

  const numTeachers = (creds: Credential[]) =>
    new Set(creds.map(cred => cred.name)).size;

  return (
    <Box>
      {isOrg ? (
        <>
          <Category
            data={certs}
            summary={t("{{count}} teacher has a teaching certificate", {
              count: numTeachers(certs),
              defaultValue_plural:
                "{{count}} teachers have teaching certificates",
            })}
            translateDegreeType={translateDegreeType}
            translateDegreeSubject={translateDegreeSubject}
            translateCredentialDiscipline={translateCredentialDiscipline}
            translateCertificateState={translateCertificateState}
          />
          <Category
            data={grad}
            summary={t("{{count}} teacher has a Graduate degree", {
              count: numTeachers(grad),
              defaultValue_plural: "{{count}} teachers have a Graduate degree",
            })}
            translateDegreeType={translateDegreeType}
            translateDegreeSubject={translateDegreeSubject}
            translateCredentialDiscipline={translateCredentialDiscipline}
            translateCertificateState={translateCertificateState}
          />
          <Category
            data={bachelors}
            summary={t("{{count}} teacher has a Bachelor’s degree", {
              count: numTeachers(bachelors),
              defaultValue_plural:
                "{{count}} teachers have a Bachelor’s degree",
            })}
            translateDegreeType={translateDegreeType}
            translateDegreeSubject={translateDegreeSubject}
            translateCredentialDiscipline={translateCredentialDiscipline}
            translateCertificateState={translateCertificateState}
          />
          <Category
            data={associates}
            summary={t("{{count}} teacher has an Associate’s degree", {
              count: numTeachers(associates),
              defaultValue_plural:
                "{{count}} teachers have an Associate’s degree",
            })}
            translateDegreeType={translateDegreeType}
            translateDegreeSubject={translateDegreeSubject}
            translateCredentialDiscipline={translateCredentialDiscipline}
            translateCertificateState={translateCertificateState}
          />
        </>
      ) : (
        <>
          {certs.length === 1 ? (
            <SingleCredential
              credential={certs[0]}
              translateDegreeType={translateDegreeType}
              translateDegreeSubject={translateDegreeSubject}
              translateCredentialDiscipline={translateCredentialDiscipline}
              translateCertificateState={translateCertificateState}
            />
          ) : (
            <Category
              data={certs}
              summary={t("{{count}} Teaching Certificate", {
                count: certs.length,
                defaultValue_plural: "{{count}} Teaching Certificates",
              })}
              translateDegreeType={translateDegreeType}
              translateDegreeSubject={translateDegreeSubject}
              translateCredentialDiscipline={translateCredentialDiscipline}
              translateCertificateState={translateCertificateState}
            />
          )}
          {[...associates, ...bachelors, ...grad].length === 1 ? (
            <SingleCredential
              credential={[...associates, ...bachelors, ...grad][0]}
              translateDegreeType={translateDegreeType}
              translateDegreeSubject={translateDegreeSubject}
              translateCredentialDiscipline={translateCredentialDiscipline}
              translateCertificateState={translateCertificateState}
            />
          ) : (
            <Category
              data={[...grad, ...bachelors, ...associates]}
              summary={t("{{count}} Degree", {
                count: associates.length + bachelors.length + grad.length,
                defaultValue_plural: "{{count}} Degrees",
              })}
              translateDegreeType={translateDegreeType}
              translateDegreeSubject={translateDegreeSubject}
              translateCredentialDiscipline={translateCredentialDiscipline}
              translateCertificateState={translateCertificateState}
            />
          )}
        </>
      )}
    </Box>
  );
}

function Category({
  data,
  summary,
  translateDegreeType,
  translateDegreeSubject,
  translateCredentialDiscipline,
  translateCertificateState,
}: {
  data: Credential[];
  summary: string;
  translateDegreeType: any;
  translateDegreeSubject: any;
  translateCredentialDiscipline: any;
  translateCertificateState: any;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\Credentials",
  );

  if (data.length === 0) {
    return null;
  }

  return (
    <Accordion>
      <Box sx={{ paddingY: 4 }}>
        <Box
          sx={{
            "&:hover": {
              borderRadius: 8,
              backgroundColor: "grey.50",
              color: "primary.700",
              textDecoration: "underline",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<Icon icon={faChevronDown} />}
            sx={{ paddingX: 8, borderRadius: 8 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
              <YellowBlobIcon icon={faMedal} />
              <Typography variant="subtitle1">{summary}</Typography>
            </Box>
          </AccordionSummary>
        </Box>
      </Box>
      <AccordionDetails
        sx={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        <Box sx={{ paddingX: 16, borderRadius: 8, backgroundColor: "grey.50" }}>
          {data.map((credential, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingY: 12,
                gap: "20px",
                "&:not(:first-of-type)": {
                  borderTop: "1px solid",
                  borderTopColor: "grey.200",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Typography variant="subtitle1">
                  {getCredentialText(
                    credential,
                    t,
                    translateDegreeType,
                    translateDegreeSubject,
                    translateCredentialDiscipline,
                    translateCertificateState,
                  )}
                </Typography>
                {credential.degreeAdditionalInfo && (
                  <AdditionalInfoTooltip
                    degreeAdditionalInfo={credential.degreeAdditionalInfo}
                  />
                )}
              </Box>
              <Typography
                variant="body1"
                sx={(theme: Theme) => ({
                  flex: 1,
                  textAlign: "right",
                  minWidth: 225,
                  textWrap: "wrap",
                  [theme.breakpoints.down("md")]: {
                    minWidth: 125,
                  },
                })}
              >
                {credential.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function SingleCredential({
  credential,
  translateDegreeType,
  translateDegreeSubject,
  translateCredentialDiscipline,
  translateCertificateState,
}: {
  credential: Credential;
  translateDegreeType: any;
  translateDegreeSubject: any;
  translateCredentialDiscipline: any;
  translateCertificateState: any;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\Credentials",
  );

  return (
    <Box sx={{ paddingY: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginLeft: 8,
          gap: 8,
        }}
      >
        <YellowBlobIcon icon={faMedal} />
        <Typography variant="subtitle2" sx={{ marginRight: 4 }}>
          {getCredentialText(
            credential,
            t,
            translateDegreeType,
            translateDegreeSubject,
            translateCredentialDiscipline,
            translateCertificateState,
          )}
        </Typography>
        {credential.degreeAdditionalInfo && (
          <AdditionalInfoTooltip
            degreeAdditionalInfo={credential.degreeAdditionalInfo}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingY: 12,
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, textAlign: "right" }}>
          {credential.name}
        </Typography>
      </Box>
    </Box>
  );
}

const AdditionalInfoTooltip = ({
  degreeAdditionalInfo,
}: {
  degreeAdditionalInfo: string;
}) => {
  const { translate } = useMachineTranslation();
  return (
    <Tooltip
      placement="top"
      title={
        <Typography variant="inherit">
          {translate(degreeAdditionalInfo)}
        </Typography>
      }
    >
      <Icon icon={faInfoCircle} color="primary" />
    </Tooltip>
  );
};

export const getCredentialText = (
  credential: Credential,
  t: any,
  translateDegreeType: any,
  translateDegreeSubject: any,
  translateCredentialDiscipline: any,
  translateCredentialState: any,
) => {
  const certificateState = translateCredentialState(
    credential.certificateState as CertificateStateKeys,
  );

  if (
    credential.degreeType &&
    credential.degreeSubject &&
    credential.degreeSubject !== DegreeSubject.Other
  ) {
    return (
      <>
        <Typography
          variant="body1"
          component="span"
          sx={{ fontWeight: "medium" }}
        >
          {t("{{degreeType}}", {
            degreeType: translateDegreeType(credential.degreeType),
          })}
        </Typography>
        {credential.degreeInstitution && (
          <Typography variant="body1" component="span">
            {t(" in {{degreeSubject}} from {{degreeInstitution}}", {
              degreeInstitution: credential.degreeInstitution,
              degreeSubject: translateDegreeSubject(credential.degreeSubject),
            })}
          </Typography>
        )}
      </>
    );
  }
  if (
    credential.degreeType &&
    (!credential.degreeSubject ||
      credential.degreeSubject === DegreeSubject.Other)
  ) {
    return (
      <>
        <Typography
          variant="body1"
          component="span"
          sx={{ fontWeight: "medium" }}
        >
          {t("{{degreeType}}", {
            degreeType: translateDegreeType(credential.degreeType),
          })}
        </Typography>
        {credential.degreeInstitution && (
          <Typography variant="body1" component="span">
            {t(" from {{degreeInstitution}}", {
              degreeInstitution: credential.degreeInstitution,
            })}
          </Typography>
        )}
      </>
    );
  }
  if (
    credential.certificateDiscipline &&
    credential.certificateDiscipline !== TeacherCertificateDiscipline.Other &&
    !credential.certificateState
  ) {
    return (
      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
        {t("{{certificateDiscipline}}", {
          certificateDiscipline: translateCredentialDiscipline(
            credential.certificateDiscipline,
          ),
        })}
      </Typography>
    );
  }
  if (
    credential.certificateDiscipline &&
    credential.certificateDiscipline !== TeacherCertificateDiscipline.Other &&
    credential.certificateState
  ) {
    return (
      <>
        <Typography
          variant="body1"
          component="span"
          sx={{ fontWeight: "medium" }}
        >
          {t("{{certificateState}} Teaching Certificate", {
            certificateState: certificateState,
          })}
        </Typography>
        <Typography variant="body1" component="span">
          {t(" in {{certificateDiscipline}}", {
            certificateDiscipline: translateCredentialDiscipline(
              credential.certificateDiscipline,
            ),
          })}
        </Typography>
      </>
    );
  }
  if (
    credential.certificateDiscipline === TeacherCertificateDiscipline.Other &&
    credential.certificateState
  ) {
    return (
      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
        {t("{{certificateState}} Teaching Certificate", {
          certificateState: certificateState,
        })}
      </Typography>
    );
  }
  return null;
};
